import React, { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Clock,
  Check,
  Star,
  ChevronsLeftRight,
  Phone,
  Layers,
  Droplets,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import { COMPANY } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

const IMG_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/gallery-images`;
const img = (name) => `${IMG_BASE}/${name}.png`;

/* ============ DATA ============ */

const SERVICES = [
  { id: "tous", label: "Tous" },
  { id: "nettoyage", label: "Nettoyage" },
  { id: "polissage", label: "Polissage" },
  { id: "ceramique", label: "Céramique" },
];

const REALIZATIONS = [
  {
    id: 1,
    service: "nettoyage",
    vehicle: "Audi RS6 Avant",
    year: "2022",
    formula: "Nettoyage Ultimate",
    initial:
      "Habitacle très encrassé après un été chargé : sable, tapis tachés, sièges tissus imprégnés. Extérieur mat avec dépôts organiques.",
    steps: [
      "Prélavage carrosserie + décontamination fer",
      "Shampoing complet sièges tissus + tapis",
      "Désinfection habitacle à la vapeur",
      "Cire hydrophobe 3 mois carrosserie",
    ],
    duration: "6h",
    result:
      "Intérieur retrouvé à 95% de son état neuf, extérieur protégé pour 3 mois. Client repart avec une voiture qui sent le neuf.",
    testimonial: "Un travail méticuleux, la voiture est méconnaissable à l'intérieur.",
    author: "Nicolas D.",
    before: img("audi_rs6_v2_before"),
    after: img("audi_rs6_v2_after"),
  },
  {
    id: 2,
    service: "polissage",
    vehicle: "Porsche 911 Carrera",
    year: "2018",
    formula: "Polissage Correction (2 étapes)",
    initial:
      "Swirls marqués sur les flancs et le capot (lavage station à rouleaux répété). Micro-hologrammes visibles sous lampe LED.",
    steps: [
      "Décontamination argile + fer-out",
      "Mesure d'épaisseur du vernis au jaugeur",
      "Polissage abrasif Menzerna 300 (pad orange)",
      "Finition Jewelling pad ultra-soft",
      "Protection sealant 6 mois",
    ],
    duration: "9h",
    result:
      "90% des défauts effacés. La profondeur du gris ardoise Porsche est totalement retrouvée. Aucun hologramme résiduel.",
    testimonial:
      "Le rendu est celui d'une voiture neuve. Difficile de croire que c'est la même auto.",
    author: "Julien M.",
    before: img("porsche_911_v3_before"),
    after: img("porsche_911_v3_after"),
  },
  {
    id: 3,
    service: "ceramique",
    vehicle: "BMW M4 Competition",
    year: "2023",
    formula: "Céramique 5 ans",
    initial:
      "Véhicule neuf sortie concession. Micro-défauts d'usine identifiés sous LED. Client souhaite figer l'état d'origine pour 5 ans.",
    steps: [
      "Décontamination complète (argile + fer-out)",
      "Polissage 3 étapes (correction 95%+)",
      "Passe de finition jewelling",
      "Dégraissage IPA",
      "Application céramique 5 ans",
      "Cure atelier 48h",
    ],
    duration: "3 jours",
    result:
      "Vernis figé en état concours pour 5 ans. Effet hydrophobe extrême, résistance chimique maximale. Certificat remis au client.",
    testimonial: "Un investissement rassurant pour une voiture qu'on garde longtemps.",
    author: "Marc L.",
    before: img("bmw_m4_v4_before"),
    after: img("bmw_m4_v4_after"),
  },
  {
    id: 4,
    service: "polissage",
    vehicle: "Tesla Model 3 Performance",
    year: "2023",
    formula: "Polissage Concours (3 étapes)",
    initial:
      "Peinture bleue avec défauts d'usine Tesla connus (orange peel, traces de préparation). Client souhaite un rendu miroir pour préparer une céramique.",
    steps: [
      "Décontamination complète",
      "Polissage 3 étapes (cut + refine + jewelling)",
      "Correction des défauts d'usine",
      "Préparation surface pour céramique",
      "Rapport photo avant/après remis",
    ],
    duration: "12h",
    result:
      "Vernis restauré à un niveau supérieur à la sortie usine. Base parfaite pour une céramique 5 ans appliquée le lendemain.",
    testimonial: null,
    author: null,
    before: img("tesla_m3_v4_before"),
    after: img("tesla_m3_v4_after"),
  },
  {
    id: 5,
    service: "nettoyage",
    vehicle: "Mercedes GLE 350d",
    year: "2021",
    formula: "Nettoyage Ultimate SUV",
    initial:
      "SUV familial avec poils d'animaux importants, tapis coffre très encrassés (transport chien + chantier). Habitacle avec odeurs persistantes.",
    steps: [
      "Aspiration + traitement anti-poils spécifique",
      "Shampoing complet sièges et coffre",
      "Désinfection habitacle à la vapeur",
      "Traitement plastiques + cuirs",
      "Cire hydrophobe carrosserie et jantes",
    ],
    duration: "7h",
    result:
      "Habitacle 100% désinfecté et sans odeur. Poils totalement retirés. Extérieur protégé 3 mois.",
    testimonial:
      "Nous n'osions plus prendre nos amis dans la voiture. Aujourd'hui c'est réglé.",
    author: "Céline B.",
    before: img("mercedes_gle_v3_before"),
    after: img("mercedes_gle_v3_after"),
  },
  {
    id: 6,
    service: "ceramique",
    vehicle: "Volkswagen Golf GTI",
    year: "2022",
    formula: "Céramique 3 ans",
    initial:
      "Véhicule de tous les jours (garage extérieur, kilométrage élevé). Petits défauts de vernis à corriger avant protection longue durée.",
    steps: [
      "Décontamination complète",
      "Polissage 2 étapes (correction 90%)",
      "Dégraissage IPA",
      "Application céramique 3 ans",
      "Cure atelier 24h",
      "Kit d'entretien + certificat",
    ],
    duration: "2 jours",
    result:
      "Protection hydrophobe pour 3 ans. Lavages simplifiés, résistance UV et fientes assurée. Certificat de garantie remis.",
    testimonial: null,
    author: null,
    before: img("vw_golf_v3_before"),
    after: img("vw_golf_v3_after"),
  },
  {
    id: 7,
    service: "polissage",
    vehicle: "Audi A4 Berline",
    year: "2020",
    formula: "Polissage Éclat (One-Step)",
    initial:
      "Voiture destinée à la revente. Peinture ternie par 4 ans d'exposition, quelques micro-rayures superficielles.",
    steps: [
      "Lavage préparatoire complet",
      "Décontamination argile + fer-out",
      "Polissage One-Step",
      "Nettoyage IPA",
      "Cire hydrophobe 3 mois",
    ],
    duration: "4h",
    result:
      "Brillance retrouvée à 70%. Photos annonce professionnelles réalisées le jour même. Véhicule vendu 800€ au-dessus de la cote.",
    testimonial:
      "Le polissage m'a permis de vendre plus cher que prévu. Retour sur investissement immédiat.",
    author: "Pierre L.",
    before: img("audi_a4_v6_before"),
    after: img("audi_a4_v6_after"),
  },
  {
    id: 8,
    service: "nettoyage",
    vehicle: "BMW Série 3 Touring",
    year: "2019",
    formula: "Nettoyage Premium",
    initial:
      "Break familial avec traces de nourriture sur banquette arrière, tapis très encrassés. Extérieur correct mais nécessite décontamination.",
    steps: [
      "Prélavage carrosserie",
      "Shampoing sièges arrière + tapis",
      "Traitement plastiques intérieur",
      "Nettoyage jantes détaillées au pinceau",
      "Désodorisant habitacle",
    ],
    duration: "4h",
    result:
      "Intérieur totalement remis à neuf. Extérieur brillant et protégé. Livraison en fin de journée comme convenu.",
    testimonial: null,
    author: null,
    before: img("bmw_touring_v2_before"),
    after: img("bmw_touring_v2_after"),
  },
  {
    id: 9,
    service: "ceramique",
    vehicle: "Mercedes AMG GT",
    year: "2021",
    formula: "Céramique 5 ans",
    initial:
      "Véhicule de collection stocké en garage. Peinture parfaite mais client souhaite une protection définitive avant la belle saison.",
    steps: [
      "Décontamination minutieuse",
      "Polissage 3 étapes + jewelling",
      "Passe de finition dédiée",
      "Application céramique 5 ans",
      "Cure atelier 48h en atmosphère contrôlée",
    ],
    duration: "3 jours",
    result:
      "Vernis figé en état concours pour 5 ans. Effet miroir sur peinture grise mate étonnant. Certificat + kit Pro remis.",
    testimonial:
      "Le rendu est incomparable. Je recommande à tous les collectionneurs de la région.",
    author: "Thomas V.",
    before: img("amg_gt_v3_before"),
    after: img("amg_gt_v3_after"),
  },
];

const METHOD_SHOTS = [
  {
    icon: Droplets,
    title: "Préparation",
    text: "Décontamination chimique et mécanique. Chaque véhicule passe par cette étape, sans exception.",
    img: "https://images.pexels.com/photos/14615262/pexels-photo-14615262.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    icon: Layers,
    title: "Correction",
    text: "Polissage sous éclairage LED d'inspection, mesure du vernis au jaugeur, panneau par panneau.",
    img: "https://images.pexels.com/photos/14908957/pexels-photo-14908957.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    icon: Sparkles,
    title: "Finition détaillée",
    text: "Nettoyage recoins, traitement plastiques, cuirs, jantes. Le détail qui distingue un atelier premium.",
    img: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: ShieldCheck,
    title: "Protection",
    text: "Application de la protection (cire, sealant ou céramique). Cure et contrôle qualité final.",
    img: "https://images.unsplash.com/photo-1605515298946-d062f2e9da53?auto=format&fit=crop&w=1200&q=80",
  },
];

/* ============ MINI BEFORE / AFTER SLIDER ============ */
const MiniBeforeAfter = ({ before, after, alt }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const handleMove = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  return (
    <div
      ref={ref}
      data-testid="mini-ba"
      className="relative w-full aspect-[4/3] overflow-hidden select-none bg-black"
      onMouseMove={(e) => e.buttons === 1 && handleMove(e.clientX)}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
    >
      <img src={after} alt={`${alt} après`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt={`${alt} avant`}
          loading="lazy"
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }}
        />
      </div>
      <div className="absolute top-0 bottom-0 w-[2px] bg-[#D90000] pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-[#D90000] flex items-center justify-center shadow-lg">
          <ChevronsLeftRight size={16} className="text-white" />
        </div>
      </div>
      <div className="absolute top-3 left-3 px-2 py-1 bg-black/70 backdrop-blur-sm text-[9px] uppercase tracking-[0.28em] text-white">
        Avant
      </div>
      <div className="absolute top-3 right-3 px-2 py-1 bg-[#D90000] text-[9px] uppercase tracking-[0.28em] text-white">
        Après
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(parseInt(e.target.value, 10))}
        aria-label={`Curseur avant après ${alt}`}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize touch-none"
      />
    </div>
  );
};

/* ============ COMPONENT ============ */
const Galerie = () => (
  <>
    <SEO
      title="Réalisations Detailing Charleroi Avant Après | Polissage & Céramique | VistaWash"
      description="Nos réalisations detailing à Charleroi & Châtelet : polissage voiture, traitement céramique, nettoyage premium. Avant/après sur BMW, Porsche, Audi, Mercedes, Tesla."
    />
    <Hero />
    <RealizationsSection />
    <MethodShots />
    <SocialProof />
    <FinalCTA />
  </>
);

/* ============ HERO ============ */
const Hero = () => (
  <section data-testid="gal-hero" className="relative pt-40 pb-16 md:pb-24 px-6 md:px-12 bg-black grain">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-px bg-[#D90000]" />
          <span className="text-[11px] uppercase tracking-[0.35em] text-[#D90000]">
            Réalisations · Detailing Charleroi
          </span>
        </div>
        <h1 className="font-impact text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.9]">
          LA <span className="text-[#D90000]">PREUVE</span>. <br />
          PAS DES PROMESSES.
        </h1>
        <p className="text-white/70 text-base md:text-lg max-w-2xl mt-8 leading-relaxed">
          Chaque véhicule ci-dessous est passé par notre atelier de Châtelet. Photos avant/après réelles, méthode documentée, durée précisée. Explorez, puis demandez votre diagnostic.
        </p>

        <div className="flex flex-wrap gap-4 mt-10">
          <a
            href="#contact"
            data-testid="gal-hero-cta"
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            Demander un diagnostic <ArrowUpRight size={16} />
          </a>
          <a
            href="#realisations"
            data-testid="gal-hero-see-cta"
            className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
          >
            Voir les réalisations
          </a>
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-white/50">
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 text-[#FBBC04]">
              {[...Array(5)].map((_, j) => (
                <Star key={j} size={14} fill="currentColor" stroke="none" />
              ))}
            </div>
            <span>5,0 · 35 avis Google</span>
          </div>
          <div className="flex items-center gap-2">
            <Check size={14} className="text-[#D90000]" /> +300 véhicules traités
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#D90000]" /> Certificat garanti sur céramique
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ REALIZATIONS ============ */
const RealizationsSection = () => {
  const [active, setActive] = useState("tous");
  const filtered = useMemo(
    () => (active === "tous" ? REALIZATIONS : REALIZATIONS.filter((r) => r.service === active)),
    [active],
  );

  return (
    <section id="realisations" data-testid="gal-realisations" className="py-16 md:py-24 px-6 md:px-12">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Fiches de réalisation</span>
            <h2 className="font-impact text-4xl md:text-5xl lg:text-[64px] leading-[0.9] mt-4">
              9 CHANTIERS. <br />
              <span className="text-[#D90000]">DOCUMENTÉS EN DÉTAIL.</span>
            </h2>
          </div>

          {/* Filtres */}
          <div
            data-testid="gal-filters"
            className="flex flex-wrap gap-2 border border-white/10 bg-[#0a0a0a] p-1.5 self-start md:self-auto"
          >
            {SERVICES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                data-testid={`gal-filter-${s.id}`}
                className={`text-[11px] uppercase tracking-[0.22em] font-bold px-4 py-2.5 transition-colors ${
                  active === s.id
                    ? "bg-[#D90000] text-white"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-6 md:gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map((r, i) => (
              <motion.article
                key={r.id}
                layout
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.55, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`realisation-${r.id}`}
                className="border border-white/10 bg-[#0a0a0a] overflow-hidden grid md:grid-cols-12 gap-0"
              >
                {/* Media */}
                <div className="md:col-span-6 relative">
                  <MiniBeforeAfter before={r.before} after={r.after} alt={r.vehicle} />
                </div>

                {/* Info */}
                <div className="md:col-span-6 p-6 md:p-10 flex flex-col">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#D90000] font-bold border border-[#D90000]/40 px-2.5 py-1">
                      {r.service === "nettoyage" && "Nettoyage"}
                      {r.service === "polissage" && "Polissage"}
                      {r.service === "ceramique" && "Céramique"}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-white/45">
                      #{String(r.id).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="font-impact text-3xl md:text-4xl leading-tight">
                    {r.vehicle}
                    <span className="text-[#D90000] ml-3">{r.year}</span>
                  </h3>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mt-2 mb-6">
                    Formule · {r.formula}
                  </div>

                  <div className="grid gap-5 mb-6">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2 font-bold">
                        État initial
                      </div>
                      <p className="text-white/70 text-sm md:text-[15px] leading-relaxed">
                        {r.initial}
                      </p>
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2 font-bold">
                        Prestation réalisée
                      </div>
                      <ul className="space-y-1.5">
                        {r.steps.map((step, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-white/85 text-sm">
                            <Check size={14} className="text-[#D90000] mt-1 shrink-0" />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center gap-6 pt-3 pb-1 border-t border-white/10">
                      <div className="flex items-center gap-2 text-sm text-white/70">
                        <Clock size={14} className="text-[#D90000]" />
                        <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Durée</span>
                        <span className="font-bold text-white">{r.duration}</span>
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2 font-bold">
                        Résultat
                      </div>
                      <p className="text-white text-sm md:text-[15px] leading-relaxed">
                        {r.result}
                      </p>
                    </div>

                    {r.testimonial && (
                      <blockquote className="border-l-2 border-[#D90000] pl-4 py-2">
                        <p className="text-white/85 italic text-sm md:text-base leading-relaxed">
                          « {r.testimonial} »
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <div className="flex gap-0.5 text-[#FBBC04]">
                            {[...Array(5)].map((_, j) => (
                              <Star key={j} size={11} fill="currentColor" stroke="none" />
                            ))}
                          </div>
                          <span className="text-[11px] uppercase tracking-[0.25em] text-white/60">
                            {r.author}
                          </span>
                        </div>
                      </blockquote>
                    )}
                  </div>

                  <a
                    href="#contact"
                    data-testid={`realisation-cta-${r.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-3 self-start bg-transparent hover:bg-[#D90000] text-white text-[11px] uppercase tracking-[0.22em] font-bold px-6 py-3 transition-colors border border-white/25 hover:border-[#D90000]"
                  >
                    Ce type de résultat sur ma voiture <ArrowUpRight size={12} />
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

/* ============ METHOD SHOTS ============ */
const MethodShots = () => (
  <section data-testid="gal-method-shots" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mb-14">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Notre méthode en images</span>
        <h2 className="font-impact text-4xl md:text-5xl lg:text-[64px] leading-[0.9] mt-6">
          LE PROCESSUS. <br />
          <span className="text-[#D90000]">PAS SEULEMENT LE RÉSULTAT.</span>
        </h2>
        <p className="text-white/60 mt-8 leading-relaxed">
          Ce qui différencie un atelier premium d'un lavage : la préparation, la méthode et la finition. Voici comment nous travaillons, étape par étape.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {METHOD_SHOTS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`gal-method-${i}`}
              className="relative aspect-[4/5] overflow-hidden group"
            >
              <img
                src={s.img}
                alt={s.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute top-4 left-4 w-11 h-11 border border-white/25 bg-black/40 backdrop-blur-sm flex items-center justify-center">
                <Icon size={18} className="text-[#D90000]" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D90000] mb-2 font-bold">
                  0{i + 1}
                </div>
                <h3 className="font-impact text-2xl md:text-3xl mb-2 leading-tight">{s.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{s.text}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============ SOCIAL PROOF ============ */
const SocialProof = () => (
  <section data-testid="gal-social" className="py-16 md:py-24 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto border border-white/10 bg-[#0a0a0a] p-8 md:p-14 grid md:grid-cols-3 gap-8 items-center">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="flex gap-0.5 text-[#FBBC04]">
            {[...Array(5)].map((_, j) => (
              <Star key={j} size={20} fill="currentColor" stroke="none" />
            ))}
          </div>
          <span className="font-impact text-3xl md:text-4xl">5,0</span>
        </div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/60">
          Note Google
        </div>
        <div className="text-white/50 text-sm mt-2">Sur 35 avis vérifiés</div>
      </div>
      <div>
        <div className="font-impact text-5xl md:text-6xl text-[#D90000] leading-none">+300</div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 mt-3">
          Véhicules traités
        </div>
        <div className="text-white/50 text-sm mt-2">Depuis l'ouverture de l'atelier</div>
      </div>
      <div>
        <div className="font-impact text-5xl md:text-6xl leading-none">95%</div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 mt-3">
          Clients recommandent
        </div>
        <div className="text-white/50 text-sm mt-2">Selon les avis Google 2024</div>
      </div>
    </div>
  </section>
);

/* ============ FINAL CTA ============ */
const FinalCTA = () => (
  <section id="contact" data-testid="gal-final-cta" className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#1a0505] to-black border-t border-[#D90000]/20">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Votre voiture, maintenant</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[84px] leading-[0.9] mt-6 mb-8">
          VOUS VENEZ DE VOIR <br />
          CE QUI EST <span className="text-[#D90000]">POSSIBLE.</span>
        </h2>
        <p className="text-white/75 leading-relaxed max-w-md mb-10">
          Envoyez-nous quelques informations sur votre véhicule. Réponse rapide par WhatsApp, lun–sam 9h–18h, avec un diagnostic honnête et un devis précis. Pas de pression commerciale.
        </p>
        <div className="flex flex-wrap gap-4 mb-10">
          <a
            href="#quote-form"
            data-testid="gal-final-devis-cta"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            Demander mon diagnostic <ArrowUpRight size={16} />
          </a>
          <a
            href={COMPANY.phone_link}
            data-testid="gal-final-phone-cta"
            className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
          >
            <Phone size={16} /> {COMPANY.phone_display}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Atelier</div>
            <div className="text-white text-sm">{COMPANY.address_line1}</div>
            <div className="text-white/70 text-sm">{COMPANY.address_line2}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Email</div>
            <a href={`mailto:${COMPANY.email}`} className="text-white text-sm hover:text-[#D90000] transition-colors">
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      <div className="md:col-span-7 bg-black/40 border border-white/10 p-8 md:p-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] mb-2">— Formulaire de diagnostic</div>
        <h3 className="font-impact text-3xl md:text-4xl mb-8 leading-tight">
          Parlez-nous de votre véhicule
        </h3>
        <QuoteForm defaultService="Diagnostic" />
      </div>
    </div>
  </section>
);

export default Galerie;
